import { useRef, useState } from "react";
import { FormikValues } from "formik";

import { businessAddressValidationSchemaWC } from "uicomponents/AddressComponent/FormUtils";
import BussinessAddressComponent from "uicomponents/AddressComponent/BusinessAddressComponent";
import { BusinessAddressFormType } from "uicomponents/AddressComponent/FormUtils";
import { BusinessAddressInitialFormValues } from "uicomponents/AddressComponent/FormUtils";
import SuggestedAddressModel from "uicomponents/SuggestedAddressModel";
import FooterButton from "uicomponents/FooterButton";

import styles from "../BussinessAddress.module.css";
import {
  useAddBussinessAddressMutation,
  useDeleteBussinessAddressMutation,
  useUpdateBussinessAddressMutation,
} from "types/generated/operations";
import { getProspectUuid } from "utils/idHelpers";
import SaveAddressList from "uicomponents/AddressComponent/SavedAddress";
import EditCard from "uicomponents/EditCard";
import { EmployeeData, SingleBusinessAddressType } from "types";
import Alert from "uicomponents/Alert";
import {
  LOADING_BUTTON_TEXT,
  DELETE_BUTTON_TEXT,
} from "pages/WorkersCompApplcation/utils/contants";
import { sectionsQuestions } from "pages/WorkersCompApplcation/utils/sectionsQuestions";
import { errorMessageMap } from "utils/helper";
import {
  trackAddressValidationEvents,
  trackFormError,
} from "utils/analytics/trackEvent";
import { Button, Dialog } from "@justworkshr/milo-components";
import EditForm from "uicomponents/EditCard/EditForm";

interface IBusinessAddressFormProps {
  data?: SingleBusinessAddressType;
  isPrimaryAddress: boolean;
  handleAddNewAddress: () => void;
  updateOfficeAddressList: (
    operation: string,
    address?: SingleBusinessAddressType,
    uuid?: string
  ) => void;
  officeAddressList?: SingleBusinessAddressType[] | null;
  updateIsEditing: (isEditing: boolean) => void;
  employeeData?: EmployeeData;
  application?: any;
}

const BusinessAddressForm = ({
  ...props
}: IBusinessAddressFormProps): JSX.Element => {
  const {
    data,
    updateOfficeAddressList,
    officeAddressList,
    updateIsEditing,
    handleAddNewAddress,
    isPrimaryAddress,
    employeeData,
  } = props;
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEditing, setIsEditing] = useState(data?.address1 ? false : true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [responseAddresses, setResponseAddresses] = useState<
    BusinessAddressFormType[]
  >([]);
  const closeDialog = () => setIsDialogOpen(false);

  const isLastElement = officeAddressList?.slice(-1)?.[0]?.uuid === data?.uuid;
  const employeeWithThisAddress = employeeData?.filter(
    (emp) =>
      emp.officeLocationId === data?.uuid && emp.isCompanyEmployee === true
  );
  const ownerWithThisAddress = employeeData?.filter(
    (emp) => emp.officeLocationId === data?.uuid && emp.isCompanyOwner === true
  );

  const ownersAttached = ownerWithThisAddress?.length || 0;
  const employeesAttached = employeeWithThisAddress?.length || 0;

  const [addOfficeLocation, { loading: addBussinessAddressLoading }] =
    useAddBussinessAddressMutation();

  const [deleteOfficeLocation, { loading: deleteBussinessAddressLoading }] =
    useDeleteBussinessAddressMutation();

  const [updateOfficeLocation, { loading: updateBussinessAddressLoading }] =
    useUpdateBussinessAddressMutation();

  const initialFormValues: BusinessAddressFormType =
    BusinessAddressInitialFormValues(data);

  const getModalTitle = (): { title: string; description: string } => {
    let modalTitle = "";
    let modalDescription = "";

    const numberOfEmployeesAttached =
      employeesAttached > 1 ? "employees are" : "employee is";
    const numberOfOwnersAttached =
      ownersAttached > 1 ? "owners are" : "owner is";

    const testVariantStepNmber = [
      "under company staff",
      "under company staff",
      "under company staff",
    ];

    const step = testVariantStepNmber;

    if (ownersAttached && employeesAttached) {
      const numberOfEmployeesAttached =
        employeesAttached > 1 ? "employees" : "employee";
      const numberOfOwnersAttached = ownersAttached > 1 ? "owners" : "owner";

      return {
        title: `${employeesAttached} ${numberOfEmployeesAttached} and ${ownersAttached} ${numberOfOwnersAttached} are attached to this office location`,
        description: `First, remove the location from their profile ${step[0]}. Then you’ll be able to delete ${data?.locationName}.`,
      };
    }

    if (ownersAttached) {
      modalTitle = `${ownersAttached} ${numberOfOwnersAttached} attached to this office location`;
      modalDescription = `First, remove the location from their profile ${step[1]}. Then you’ll be able to delete ${data?.locationName}.`;
    }
    if (employeesAttached) {
      modalTitle = `${employeesAttached} ${numberOfEmployeesAttached} attached to this office location`;
      modalDescription = `First, remove the location from their profile ${step[2]}. Then you’ll be able to delete ${data?.locationName}.`;
    }

    return { title: modalTitle, description: modalDescription };
  };
  const toggleEditing = () => {
    updateIsEditing(!isEditing);
    setIsEditing(!isEditing);
  };

  const handleOnSubmit = async (values: BusinessAddressFormType) => {
    try {
      const hasAddressInList = officeAddressList?.find(
        (address: SingleBusinessAddressType) => address.uuid === values.uuid
      );

      let responseData;
      const isExistingAddress =
        (hasAddressInList && hasAddressInList.uuid !== "") ||
        responseAddresses.length > 0;

      const inputQueryVariables = {
        address1: values.address1,
        address2: values.address2,
        city: values.city,
        zip: values.zip,
        state: values.state,
        locationName: values.locationName,
        subdivision: values.subdivision,
      };

      if (isExistingAddress) {
        // update Address
        const updateQueryResponse = await updateOfficeLocation({
          variables: {
            uuid: hasAddressInList?.uuid || values.uuid,
            isHq: values.isHq,
            ...inputQueryVariables,
          },
        });
        responseData = updateQueryResponse.data?.updateOfficeLocation;
      } else {
        // Add Address
        const createQueryResponse = await addOfficeLocation({
          variables: {
            uuid: getProspectUuid(),
            isHq: isPrimaryAddress ? true : false,
            ...inputQueryVariables,
          },
        });
        responseData = createQueryResponse.data?.createOfficeLocation;
      }

      const uuid = responseData?.uuid || "";
      const isHqAddress = isExistingAddress ? values.isHq : !!isPrimaryAddress;

      const autoValidated = responseData?.autoValidated;

      const newAddress = {
        uuid: uuid,
        address1: values.address1,
        address2: values.address2,
        city: values.city,
        zip: values.zip,
        state: values.state,
        locationName: values.locationName,
        isHq: isHqAddress || false,
        autoValidated: autoValidated,
        subdivision: values.subdivision,
      };

      if (autoValidated === false) {
        trackAddressValidationEvents(
          "usps could not validate the address or provide suggested address"
        );
      }

      if (autoValidated === false && responseData?.suggestedAddress?.address1) {
        const suggestedAddress = {
          address1: responseData?.suggestedAddress?.address1 || "",
          address2: responseData?.suggestedAddress?.address2 || "",
          city: responseData?.suggestedAddress?.city || "",
          zip: responseData?.suggestedAddress?.zip || "",
          state: responseData?.suggestedAddress?.state || "",
          locationName: values.locationName || "",
          isHq: isHqAddress || false,
          uuid: uuid,
          subdivision: values.subdivision,
        };

        setResponseAddresses([newAddress, suggestedAddress]);
        return;
      }

      toggleEditing();
      updateOfficeAddressList("Add", newAddress);
      setIsError(false);
    } catch (error: any) {
      const errorMessage: string = error?.message;
      setError(error, errorMessage);
      return;
    }
  };

  const handleDeleteAddress = async () => {
    try {
      if (!data?.uuid) {
        updateOfficeAddressList("Delete", undefined, data?.uuid);
        return;
      }
      await deleteOfficeLocation({
        variables: {
          uuid: data.uuid,
        },
      });
      updateOfficeAddressList("Delete", undefined, data.uuid);
      setIsError(false);
    } catch (error: any) {
      const errorMessage: string = error?.message;
      setError(error, errorMessage);
      return;
    }
  };

  const setError = (error: Error, errorMessage: string) => {
    trackFormError(error, errorMessage);
    if (errorMessage.includes("Failed to delete office location")) {
      setIsDialogOpen(true);
    } else {
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      setIsError(true);
    }
  };

  const validateInlineErrors = (values: FormikValues) => {
    let errors: any = {};
    const filterCurrentAddress = officeAddressList?.filter(
      (item) => item.uuid !== (values as BusinessAddressFormType)?.uuid
    );
    const hasSameOfficeLocationName = filterCurrentAddress?.some(
      (address) => address.locationName === values.locationName
    );
    if (hasSameOfficeLocationName) {
      errors.locationName =
        "You already have an office with that name. Pick a new one.";
    }
    if (isPrimaryAddress && !values.subdivision) {
      errors.subdivision = "You missed this one";
    }
    Object.keys(values).forEach((value: any) => {
      // check typeof to add validation only for text input and textarea (only for string inputs)
      if (
        typeof values[value] === "string" &&
        values[value]?.trim().length !== values[value]?.length
      ) {
        errors[value] = "Be sure to remove any extra spaces from your entry";
      }
    });
    return errors;
  };

  const handleUseSuggestion = (address: BusinessAddressFormType) => {
    setResponseAddresses([]);
    handleOnSubmit(address);
  };

  const handleKeepCurrent = (address: BusinessAddressFormType) => {
    toggleEditing();
    updateOfficeAddressList("Add", {
      ...address,
      isHq: address.isHq || false,
    });
    setIsError(false);
    setResponseAddresses([]);
  };

  const fieldRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Helper function that makes the browser scroll a specific field into view
  const focusField = (fieldName: string) => {
    if (fieldRefs.current[fieldName]) {
      fieldRefs.current[fieldName]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  return (
    <>
      {responseAddresses.length === 2 && (
        <SuggestedAddressModel
          onKeepCurrent={handleKeepCurrent}
          onUseSuggestion={handleUseSuggestion}
          enteredAddress={responseAddresses[0]}
          suggestedAddress={responseAddresses[1]}
        />
      )}
      {isEditing && (
        <EditForm
          initialFormValues={initialFormValues}
          validationSchema={businessAddressValidationSchemaWC}
          handleOnSubmit={handleOnSubmit}
          validateInlineErrors={validateInlineErrors}
          title={
            isPrimaryAddress
              ? sectionsQuestions["officeLocation"].editCardTitlePrimary
              : sectionsQuestions["officeLocation"].editCardTitleOther
          }
          description={
            isPrimaryAddress
              ? sectionsQuestions["officeLocation"]
                  .openEditCardPrimaryDescription
              : ""
          }
          nextButtonText={
            updateBussinessAddressLoading || addBussinessAddressLoading
              ? LOADING_BUTTON_TEXT
              : "Save"
          }
          handleBackClick={() => toggleEditing()}
          disabled={addBussinessAddressLoading || updateBussinessAddressLoading}
          isError={isError}
          errorMessage={errorMessage}
          hasOnlyNextButton={isPrimaryAddress && !officeAddressList?.length}
          focusField={focusField}
        >
          <BussinessAddressComponent
            fieldRefs={fieldRefs}
            isPrimaryAddress={isPrimaryAddress}
          />
        </EditForm>
      )}
      {!isEditing && (
        <EditCard
          title={
            isPrimaryAddress
              ? sectionsQuestions["officeLocation"].editCardTitlePrimary
              : sectionsQuestions["officeLocation"].editCardTitleOther
          }
          onEditClick={() => toggleEditing()}
          description={
            isPrimaryAddress
              ? sectionsQuestions["officeLocation"]
                  .closedEditCardPrimaryDescription
              : ""
          }
        >
          <SaveAddressList data={data} />
          {isPrimaryAddress && officeAddressList?.length === 1 && (
            <FooterButton
              nextButtonText={
                deleteBussinessAddressLoading
                  ? LOADING_BUTTON_TEXT
                  : sectionsQuestions["officeLocation"].addMoreText
              }
              hasOnlyNextButton={true}
              backButtonText={DELETE_BUTTON_TEXT}
              handleBackClick={handleDeleteAddress}
              handleNextClick={handleAddNewAddress}
              disabled={deleteBussinessAddressLoading}
            />
          )}
          {!isPrimaryAddress && (
            <FooterButton
              nextButtonText={
                deleteBussinessAddressLoading
                  ? LOADING_BUTTON_TEXT
                  : sectionsQuestions["officeLocation"].addMoreText
              }
              hasOnlyBackButton={!isLastElement}
              backButtonText={DELETE_BUTTON_TEXT}
              handleBackClick={handleDeleteAddress}
              handleNextClick={handleAddNewAddress}
              disabled={deleteBussinessAddressLoading}
            />
          )}
          {isError && <Alert message={errorMessage} allowAutoScroll={false} />}
          <Dialog
            isOpen={isDialogOpen}
            onClose={closeDialog}
            showCloseButton={false}
            primaryButton={
              <Button onClick={closeDialog} variant="filled">
                Got it
              </Button>
            }
          >
            <h3 className={styles.modalTitle}>{getModalTitle().title}</h3>
            <p>{getModalTitle().description}</p>
          </Dialog>
        </EditCard>
      )}
    </>
  );
};

export default BusinessAddressForm;
